import React, { useState } from 'react';
import {
  FormField,
  Input,
  SpaceBetween,
  Button,
  Link,
  Box,
  Table,
  StatusIndicator,
  Modal,
  Container,
  Header,
} from '@cloudscape-design/components';
import { AuditTooltipMessages, SourceSopLabels } from 'src/contstant/commonLabels';
import { isSourceSopLink } from 'src/utils/docLinkUtils';
import Tooltip from '../Tooltip';
import DocLinkTitle from './DocLinkTitle';

export interface DocumentLinkItem {
  heading: string;
  url: string;
  title?: string;
  /** Carried through from `DocLink.isSourceSop` so editing doesn't drop the
   *  source-SOP marker (see Documentation step). */
  isSourceSop?: boolean;
}

interface DocumentLinkProps {
  links: DocumentLinkItem[];
  onChange: (links: DocumentLinkItem[]) => void;
}

/**
 * The links with the row at `index` removed — unless that row is the source
 * SOP (system-attached provenance, never removable) or doesn't exist, in which
 * case the SAME array is returned so callers can tell nothing changed. Pure, so
 * the guard is enforced (and testable) independently of the disabled button.
 */
export function removeDocumentLinkAt(
  links: DocumentLinkItem[],
  index: number,
): DocumentLinkItem[] {
  const target = links.find((_, i) => i === index);
  if (target === undefined || isSourceSopLink(target)) return links;
  return links.filter((_, i) => i !== index);
}

const DocumentLink: React.FC<DocumentLinkProps> = ({ links = [], onChange }) => {
  const [newLink, setNewLink] = useState<DocumentLinkItem>({
    heading: '',
    url: '',
    title: '',
  });
  const [isModalVisible, setIsModalVisible] = useState(false);

  const handleAddLink = () => {
    if (newLink.url && newLink.heading) {
      // Only include title if it has a value
      const linkToAdd = {
        heading: newLink.heading,
        url: newLink.url,
        ...(newLink.title ? { title: newLink.title } : {}),
      };
      const updatedLinks = [...links, linkToAdd];
      onChange(updatedLinks);
      // Reset form
      setNewLink({
        heading: '',
        url: '',
        title: '',
      });

      setIsModalVisible(false);
    }
  };

  const handleRemoveLink = (index: number) => {
    const updatedLinks = removeDocumentLinkAt(links, index);
    // Same reference = nothing removed (source-SOP row, or no such row).
    if (updatedLinks !== links) onChange(updatedLinks);
  };

  function isValidUrl(url: string) {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  const isAddButtonDisabled =
    !newLink.url || !newLink.heading || !isValidUrl(newLink.url);

  return (
    <Container
      data-testid="document-link-container"
      header={
        <Header
          data-testid="document-link-header"
          variant="h3"
          actions={
            <Button
              data-testid="add-document-link-button"
              onClick={() => setIsModalVisible(true)}
            >
              Add Document Link
            </Button>
          }
          info={<Tooltip content={AuditTooltipMessages.DOCUMENTATION} />}
        >
          Standard Operating Procedures
        </Header>
      }
    >
      <Table
        data-testid="document-links-table"
        variant="embedded"
        columnDefinitions={[
          {
            id: 'document',
            header: 'Document',
            cell: (item) => (
              <DocLinkTitle title={item.heading} isSourceSop={isSourceSopLink(item)} />
            ),
          },
          {
            id: 'link',
            header: 'Link',
            maxWidth: 300,
            cell: (item) => (
              <Link data-testid="document-link" href={item.url}>
                {item.url}
              </Link>
            ),
          },
          {
            id: 'actions',
            header: 'Actions',
            cell: (item: DocumentLinkItem) => {
              // The table hands back the same row objects it was given, so
              // indexOf resolves THIS row exactly. A url+heading match would
              // pick the first look-alike — e.g. an author's copy of the SOP
              // link — and the source-SOP guard would then block removing it.
              const index = links.indexOf(item);
              const isLocked = isSourceSopLink(item);
              // Icon button (trash), matching the V2 review table. Cloudscape
              // only honours `disabledReason` on normal/primary/icon variants
              // (not link/inline-link), and it's what explains — on hover and
              // keyboard focus — why the source-SOP row can't be removed.
              return (
                <Button
                  data-testid="remove-link-button"
                  variant="icon"
                  iconName="remove"
                  ariaLabel={`Remove ${item.heading}`}
                  disabled={isLocked}
                  {...(isLocked
                    ? { disabledReason: SourceSopLabels.REMOVE_DISABLED_REASON }
                    : {})}
                  onClick={() => handleRemoveLink(index)}
                />
              );
            },
          },
        ]}
        items={links}
        empty={
          <Box
            data-testid="empty-state"
            margin={{ vertical: 'xs' }}
            textAlign="center"
            color="inherit"
          >
            <SpaceBetween size="m">
              <StatusIndicator data-testid="no-data-indicator" type="warning">
                No Data.
              </StatusIndicator>
            </SpaceBetween>
          </Box>
        }
        stripedRows
        stickyHeader
      />

      {/* Modal for adding links in popup mode */}

      <Modal
        data-testid="add-document-link-modal"
        visible={isModalVisible}
        onDismiss={() => setIsModalVisible(false)}
        header="Add Document Link"
        footer={
          <Box data-testid="modal-footer" float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                data-testid="cancel-button"
                variant="link"
                onClick={() => setIsModalVisible(false)}
              >
                Cancel
              </Button>
              <Button
                data-testid="add-link-button"
                variant="primary"
                onClick={handleAddLink}
                disabled={isAddButtonDisabled}
              >
                Add
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="s">
          <FormField data-testid="document-title-field" label="Document Title">
            <Input
              data-testid="document-title-input"
              value={newLink.title || ''}
              onChange={({ detail }) =>
                setNewLink({ ...newLink, title: detail.value, heading: detail.value })
              }
              placeholder="Enter document title"
            />
          </FormField>

          <FormField
            data-testid="url-field"
            label="URL"
            errorText={
              newLink.url && !isValidUrl(newLink.url)
                ? 'Please enter a valid URL'
                : undefined
            }
          >
            <Input
              data-testid="url-input"
              value={newLink.url}
              onChange={({ detail }) => setNewLink({ ...newLink, url: detail.value })}
              placeholder="https://example.com"
            />
          </FormField>
        </SpaceBetween>
      </Modal>
    </Container>
  );
};

export default DocumentLink;
